/**
 * Tab-isolated session identifier.
 *
 * Each browser tab gets a unique id stored in sessionStorage. It is sent as
 * `X-Tab-Id` on every API request so the server can look up the session for this
 * tab specifically — which is what lets one browser hold several different
 * accounts at once, despite `ghq_sid` being a single cookie shared across every
 * subdomain.
 *
 * sessionStorage is cleared when the tab closes; the tab's mapping in Redis then
 * expires on its own TTL.
 */

/**
 * A high-entropy id that does not depend on a secure context.
 *
 * `crypto.randomUUID` is undefined on plain HTTP and in some embedded webviews —
 * which is exactly the population that also gets storage restrictions, so the
 * fallback path below must not depend on it. `getRandomValues` is far more widely
 * available; the last resort is only reached in an environment that has no crypto
 * at all, and still must not collide.
 */
function newTabId(): string {
  const c: Crypto | undefined = globalThis.crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  if (typeof c?.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

/**
 * Held for this page load only, for browsers that refuse sessionStorage —
 * Safari private mode, in-app browsers inside Instagram or WhatsApp, blocked
 * site data, enterprise policy.
 *
 * This used to be the literal string `'no-tab-id'`, which every such browser
 * returned. They all shared one Redis key, and because a tab mapping overrode
 * the session cookie, whoever signed in last owned it and everyone else on that
 * key was served THAT person's account. Reproduced 2026-09-16: user A, holding a
 * valid cookie of their own, was shown user B's email and organization.
 *
 * A per-load random id costs only the ability to tell two tabs apart within one
 * such browser. It can never hand one person another person's session.
 */
let inMemoryTabId: string | null = null;

export function getTabId(): string {
  try {
    let tabId = sessionStorage.getItem('ghq_tab_id');
    if (!tabId) {
      tabId = newTabId();
      sessionStorage.setItem('ghq_tab_id', tabId);
    }
    return tabId;
  } catch {
    return (inMemoryTabId ??= newTabId());
  }
}

/** Test seam — forgets the in-memory fallback so a spec can simulate a fresh load. */
export function __resetTabId(): void {
  inMemoryTabId = null;
}
